import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetInventoryVehicleMaintenance } from '@/api/generated/inventory-vehicles/inventory-vehicles';
import { VehicleMaintenanceStatus } from '@/api/generated/schemas';
import { Badge, Button, EmptyState, Panel, Table } from '@/shared/ui';

import {
  emptyVehicleMaintenanceFormValues,
  fromVehicleMaintenanceItem,
  type VehicleMaintenanceFormValues,
} from '../vehicleMaintenanceFormSchema';
import { VehicleMaintenanceDialog } from './VehicleMaintenanceDialog';

function statusTone(status: VehicleMaintenanceStatus) {
  if (status === VehicleMaintenanceStatus.Ongoing) return 'info' as const;
  if (status === VehicleMaintenanceStatus.Completed) return 'success' as const;
  return 'neutral' as const;
}

/**
 * Záložka Maintenance — samostatný agregát s vlastním uložením v dialogu (stejně jako Documents).
 * Formulář vozu servisní záznamy nedrží, takže globální `FormActionBar` se sem nevztahuje.
 */
export function VehicleMaintenanceTab({ vehicleId }: { vehicleId?: number }) {
  const { t, i18n } = useTranslation('inventory');
  const [draft, setDraft] = useState<VehicleMaintenanceFormValues | null>(null);

  const maintenanceQuery = useGetInventoryVehicleMaintenance(vehicleId ?? 0, {
    query: { enabled: vehicleId != null },
  });

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language]
  );
  const moneyFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'PLN' }),
    [i18n.language]
  );

  // Servisní záznam visí na uloženém voze (FK na dbo.Vehicle.FacilityID), takže v create režimu
  // nemá kam patřit — stejně jako u Documents.
  if (vehicleId == null) {
    return (
      <EmptyState
        icon="wrench"
        title={t('maintenance.saveVehicleFirstTitle')}
        description={t('maintenance.saveVehicleFirstDescription')}
      />
    );
  }

  const items = maintenanceQuery.data?.items ?? [];

  return (
    <>
      <Panel
        variant="filled"
        icon="wrench"
        title={t('vehicle.tabs.maintenance')}
        actions={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconLeft="plus"
            onClick={() => setDraft(emptyVehicleMaintenanceFormValues())}
          >
            {t('maintenance.add')}
          </Button>
        }
      >
        {maintenanceQuery.isLoading ? (
          <p className="text-phx-text-muted text-phx-sm">{t('maintenance.loading')}</p>
        ) : maintenanceQuery.isError ? (
          <EmptyState icon="alert" title={t('maintenance.loadError')} />
        ) : items.length === 0 ? (
          <EmptyState icon="wrench" title={t('maintenance.empty')} />
        ) : (
          <div className="border-phx-border rounded-phx overflow-x-auto border">
            <Table density="dense">
              <Table.Head>
                <Table.Row>
                  <Table.HCell density="dense">{t('maintenance.cols.id')}</Table.HCell>
                  <Table.HCell density="dense">{t('maintenance.cols.date')}</Table.HCell>
                  <Table.HCell density="dense">{t('maintenance.cols.type')}</Table.HCell>
                  <Table.HCell density="dense">{t('maintenance.cols.description')}</Table.HCell>
                  <Table.HCell density="dense">{t('maintenance.cols.status')}</Table.HCell>
                  <Table.HCell density="dense" className="text-right">
                    {t('maintenance.cols.cost')}
                  </Table.HCell>
                  <Table.HCell density="dense" className="w-10" />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell density="dense" className="font-mono">
                      {item.reference}
                    </Table.Cell>
                    <Table.Cell density="dense" className="whitespace-nowrap">
                      {dateFormatter.format(new Date(item.startDate))} –{' '}
                      {dateFormatter.format(new Date(item.endDate))}
                    </Table.Cell>
                    <Table.Cell density="dense">{item.typeName ?? '—'}</Table.Cell>
                    <Table.Cell density="dense">{item.description}</Table.Cell>
                    <Table.Cell density="dense">
                      <Badge tone={statusTone(item.status)}>
                        {t(`maintenance.status.${item.status.toLowerCase()}`)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell
                      density="dense"
                      className="text-right whitespace-nowrap tabular-nums"
                    >
                      {moneyFormatter.format(item.costPln)}
                    </Table.Cell>
                    <Table.Cell density="dense" className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        iconLeft="edit"
                        aria-label={t('maintenance.edit')}
                        onClick={() => setDraft(fromVehicleMaintenanceItem(item))}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Panel>

      {draft ? (
        <VehicleMaintenanceDialog
          vehicleId={vehicleId}
          initialValues={draft}
          onClose={() => setDraft(null)}
        />
      ) : null}
    </>
  );
}
